import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProjectsStore } from "@/stores/projects";
import { useUiStore } from "@/stores/ui";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Project } from "@/core/state-shape";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const ProjectCard = ({
  project,
  onSelect,
  onRemove,
  isActive,
}: {
  project: Project;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  isActive: boolean;
}) => (
  <Card className={isActive ? "border-primary" : ""}>
    <CardHeader>
      <CardTitle>{project.name}</CardTitle>
      <CardDescription className="font-mono text-xs truncate">{project.serverPath}</CardDescription>
    </CardHeader>
    <CardContent className="flex flex-wrap gap-2">
      <Badge variant="outline">{project.provider || 'local'}</Badge>
      <Badge variant={project.connectionStatus === "connected" ? "default" : "secondary"}>
        {project.connectionStatus}
      </Badge>
      <Badge variant={project.fileIndexStatus === "indexed" ? "default" : "secondary"}>
        Index: {project.fileIndexStatus}
      </Badge>
    </CardContent>
    <CardFooter className="flex justify-end gap-2">
      <Button variant="ghost" size="sm" onClick={() => onRemove(project.id)}>
        Remove
      </Button>
      <Button size="sm" onClick={() => onSelect(project.id)} disabled={isActive}>
        {isActive ? "Selected" : "Select"}
      </Button>
    </CardFooter>
  </Card>
);

const ProjectCardSkeleton = () => (
  <Card>
    <CardHeader>
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-full mt-2" />
    </CardHeader>
    <CardContent className="flex gap-2">
      <Skeleton className="h-6 w-16" />
      <Skeleton className="h-6 w-24" />
      <Skeleton className="h-6 w-20" />
    </CardContent>
    <CardFooter className="flex justify-end gap-2">
      <Skeleton className="h-9 w-20" />
      <Skeleton className="h-9 w-20" />
    </CardFooter>
  </Card>
);

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { list, byId, loadProjects, selectProject, removeProject, createProject, activeProjectId } = useProjectsStore();
  const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);

  // State for the new project form
  const [formState, setFormState] = useState({
    name: "",
    serverPath: "",
    proxyPort: 3000,
    sessionName: "merged-dev",
    backendCommand: "python run_server_windows.py",
    backendDirectory: "backend",
    frontendCommand: "npm run dev",
    frontendDirectory: ".",
    windowsShell: "cmd",
    precommand: "",
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    setHeaderTitle("Projects");
    loadProjects();
  }, [setHeaderTitle, loadProjects]);

  const handleSelect = async (projectId: string) => {
    try {
      await selectProject(projectId);
      navigate("/pages/workspace/");
    } catch (e: any) {
      toast.error("Failed to select project", { description: e.message });
    }
  };

  const handleRemove = async (projectId: string) => {
    if (window.confirm("Are you sure you want to remove this project? This cannot be undone.")) {
      try {
        await removeProject(projectId);
      } catch (e: any) {
        toast.error("Failed to remove project", { description: e.message });
      }
    }
  };
  
  const handleAddProject = async () => {
    if (!formState.name.trim() || !formState.serverPath.trim()) {
      toast.error("Project name and path are required.");
      return;
    }
    try {
      await createProject({ 
        name: formState.name, 
        serverPath: formState.serverPath,
        proxyPort: formState.proxyPort || undefined,
        devSessionConfig: {
            sessionName: formState.sessionName,
            windowsShell: formState.windowsShell,
            precommand: formState.precommand,
            startup: [
                { name: "backend", command: formState.backendCommand, directory: formState.backendDirectory },
                { name: "frontend", command: formState.frontendCommand, directory: formState.frontendDirectory }
            ]
        }
      });
      // Reset form state after successful creation
      setFormState({ name: "", serverPath: "", proxyPort: 3000, sessionName: "merged-dev", backendCommand: "python run_server_windows.py", backendDirectory: "backend", frontendCommand: "npm run dev", frontendDirectory: ".", windowsShell: 'cmd', precommand: "" });
      setIsDialogOpen(false);
    } catch (e: any) {
      toast.error("Failed to add project", { description: e.message });
    }
  };

  const projects = list.map((id) => byId[id]).filter(Boolean);

  return (
    <div className="container mx-auto p-4 lg:p-6 space-y-6">
      <header className="flex justify-between items-center">
        <div className="hidden md:block">
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-muted-foreground">Select a project to work on or add a new one.</p>
        </div>
        
        <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <AlertDialogTrigger asChild>
            <Button>Add New Project</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
          <AlertDialogHeader>
              <AlertDialogTitle>Add a New Project</AlertDialogTitle>
              <AlertDialogDescription>
                Configure your new project and its development session commands.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">Name</Label>
                <Input id="name" value={formState.name} onChange={(e) => setFormState(s => ({...s, name: e.target.value}))} className="col-span-3" placeholder="e.g., My Awesome Project" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="path" className="text-right">Path</Label>
                <Input id="path" value={formState.serverPath} onChange={(e) => setFormState(s => ({...s, serverPath: e.target.value}))} className="col-span-3" placeholder="/path/to/your/project" />
              </div>
              <Separator className="my-2" />
              <h4 className="font-semibold text-sm">Application Proxy</h4>
               <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="proxyPort" className="text-right">App Port</Label>
                <Input id="proxyPort" type="number" value={formState.proxyPort} onChange={(e) => setFormState(s => ({...s, proxyPort: parseInt(e.target.value, 10) || 0}))} className="col-span-3" placeholder="e.g., 3000" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <p className="col-start-2 col-span-3 text-xs text-muted-foreground -mt-2">The local port your application runs on. A proxy will listen on 8004 and forward here.</p>
              </div>
              <Separator className="my-2" />
              <h4 className="font-semibold text-sm">Dev Session Config</h4>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="sessionName" className="text-right">Session Name</Label>
                <Input id="sessionName" value={formState.sessionName} onChange={(e) => setFormState(s => ({...s, sessionName: e.target.value}))} className="col-span-3" />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">Windows Shell</Label>
                <div className="col-span-3">
                    <RadioGroup
                        value={formState.windowsShell}
                        onValueChange={(value) => setFormState(s => ({...s, windowsShell: value}))}
                        className="flex space-x-4"
                    >
                        <div className="flex items-center space-x-2"><RadioGroupItem value="cmd" id="new-shell-cmd" /><Label htmlFor="new-shell-cmd">CMD</Label></div>
                        <div className="flex items-center space-x-2"><RadioGroupItem value="powershell" id="new-shell-ps" /><Label htmlFor="new-shell-ps">PowerShell</Label></div>
                        <div className="flex items-center space-x-2"><RadioGroupItem value="winbash" id="new-shell-bash" /><Label htmlFor="new-shell-bash">Bash</Label></div>
                    </RadioGroup>
                    <p className="text-xs text-muted-foreground mt-1">Bash requires `WINBASH_PATH` env var on server.</p>
                </div>
              </div>
              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="precommand" className="text-right pt-2">Prerun Script</Label>
                <div className="col-span-3">
                    <Textarea
                        id="precommand"
                        value={formState.precommand}
                        onChange={(e) => setFormState(s => ({...s, precommand: e.target.value}))}
                        className="font-mono text-xs"
                        placeholder="e.g., source .venv/bin/activate"
                        rows={2}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Runs before commands. Use `$PROJECT_ROOT` for server path.</p>
                </div>
              </div>
               <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="be-cmd" className="text-right">Backend Cmd</Label>
                <Input id="be-cmd" value={formState.backendCommand} onChange={(e) => setFormState(s => ({...s, backendCommand: e.target.value}))} className="col-span-3" />
              </div>
               <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="be-dir" className="text-right">Backend Dir</Label>
                <Input id="be-dir" value={formState.backendDirectory} onChange={(e) => setFormState(s => ({...s, backendDirectory: e.target.value}))} className="col-span-3" />
              </div>
               <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="fe-cmd" className="text-right">Frontend Cmd</Label>
                <Input id="fe-cmd" value={formState.frontendCommand} onChange={(e) => setFormState(s => ({...s, frontendCommand: e.target.value}))} className="col-span-3" />
              </div>
               <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="fe-dir" className="text-right">Frontend Dir</Label>
                <Input id="fe-dir" value={formState.frontendDirectory} onChange={(e) => setFormState(s => ({...s, frontendDirectory: e.target.value}))} className="col-span-3" />
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleAddProject}>Add Project</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </header>

      {projects.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed rounded-lg">
          <h2 className="text-xl font-semibold">No Projects Found</h2>
          <p className="mt-2">Add a new project to get started.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} onSelect={handleSelect} onRemove={handleRemove} isActive={p.id === activeProjectId} />
          ))}
        </div>
      )}
    </div>
  );
}